import { router } from "@inertiajs/react"
import { Info } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { useCurrentContext } from "@/hooks/use-current-context"
import { analyticsNoticePath } from "@/routes"

/**
 * AnalyticsNoticeBanner — OKS-532.
 *
 * App-wide, non-blocking product-analytics disclosure. Mounted ONCE in
 * `app-layout.tsx` (above the role-shell switch, beside `<ActingAsBanner>`)
 * so it covers every authenticated shell — creator / brand / agency / admin —
 * with a single instance (UX Spec §2).
 *
 * Renders ONLY when an authenticated user has NOT yet dismissed the notice
 * (`currentUser && !currentUser.analytics_notice_seen`). Returns `null` (no
 * DOM) for anonymous visitors, for accounts that already tapped "Got it", and
 * after an optimistic local dismiss. Notice basis, NOT consent: there is no
 * opt-in/opt-out — dismissing only hides the banner (capture is OKS-533).
 *
 * Dismiss is OPTIMISTIC + server-persisted (NO localStorage, Crew rule):
 * tapping "Got it" hides the banner immediately and PATCHes
 * `analyticsNoticePath()` in the background, which stamps
 * `users.analytics_notice_seen_at`. The success redirect recomputes
 * `analytics_notice_seen` (now true) server-side, so the banner stays gone on
 * every later load, on every device. On persist FAILURE we fail safe: revert
 * the local collapse so the notice silently re-shows on the next load — no
 * toast, never blocking (PRD §6 / UX Spec §3 error state).
 *
 * The Privacy Policy link is a plain external `<a target="_blank"
 * rel="noopener noreferrer">` (the policy lives off-repo on oks.studio — PRD
 * Open Q1), URL from the shared `privacyUrl` context prop.
 *
 * Brand isolation: per-user, carries no brand data, runs no cross-brand
 * query — zero isolation surface.
 *
 * Mobile overflow (OKS-607): the shared `Alert` primitive is a CSS grid
 * whose content column is `1fr` (grid-item default `min-width: auto`, so it
 * cannot shrink below its content's min-content width). Without intervention
 * the long notice sentence forced the column — and the whole document — wider
 * than narrow viewports (~632px @375px). We break that chain HERE, at the
 * banner rather than with a ledgered `ui/**` edit (permitted with parity/a11y
 * coverage — see `docs/frontend/README.md` rule 6 — but fixing it here is
 * cheaper): `min-w-0` on the
 * `AlertDescription` grid item lets the `1fr` track shrink below min-content
 * and wrap; `min-w-0` on the inner flex `<span>` lets the text column shrink;
 * `break-words` on the Privacy Policy link lets long localized link text break
 * across lines. Net effect: no horizontal scroll down to 320px, content
 * unchanged.
 *
 * Accessibility: a labeled landmark — `role="region"` +
 * `aria-label` (persistent, static disclosure, NOT `alertdialog`, which would
 * read as a consent gate, UX Spec §6). It deliberately does NOT use
 * `role="status"` / `aria-live`: those are for transient live-region updates
 * (toasts), and permanently occupying that role collides with real toasts'
 * `getByRole("status")` queries. The Info icon + text carry meaning
 * independent of the tint; the dismiss button has an explicit `aria-label`.
 */
export function AnalyticsNoticeBanner() {
  const { t } = useTranslation()
  const { currentUser, privacyUrl } = useCurrentContext()
  const [dismissed, setDismissed] = useState(false)

  if (!currentUser || currentUser.analytics_notice_seen) return null
  if (dismissed) return null

  function handleDismiss() {
    // Optimistic collapse — hide immediately, persist in the background.
    setDismissed(true)
    router.patch(
      analyticsNoticePath(),
      {},
      {
        preserveScroll: true,
        onError: () => {
          // Persist failed — fail safe: re-show the notice on the next
          // paint. No toast, never blocking (UX Spec §3 error state).
          setDismissed(false)
        },
      },
    )
  }

  return (
    <Alert
      variant="default"
      role="region"
      aria-label={t("analytics_notice.aria_label")}
      data-testid="analytics-notice-banner"
      className="rounded-none border-x-0 border-t-0"
    >
      <Info aria-hidden="true" />
      <AlertDescription className="flex min-w-0 flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-3">
        <span className="min-w-0 flex-1">
          {t("analytics_notice.body.prefix")}
          <a
            href={privacyUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="analytics-notice-privacy-link"
            className="font-medium break-words underline decoration-current/60 underline-offset-4 hover:decoration-current"
          >
            {t("analytics_notice.body.privacy_link")}
          </a>
          {t("analytics_notice.body.suffix")}
        </span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleDismiss}
          aria-label={t("analytics_notice.dismiss_aria")}
          data-testid="analytics-notice-dismiss"
          className="min-h-[44px] shrink-0 self-start sm:min-h-0 sm:self-auto"
        >
          {t("analytics_notice.dismiss")}
        </Button>
      </AlertDescription>
    </Alert>
  )
}
